import { Select } from 'aha-components';
import GA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import {
  type ScreeningQuestion,
  useMultiplePricesStore
} from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { getOrderSource } from '@/biz/tool';
import SimpleRichText from '@/components/SimpleRichText';
import RichTextHtmlViewer from '@/components/RichTextHtmlViewer';
import DisqualifiedAnswerAlert from './DisqualifiedAnswerAlert';
import {
  isScreeningAnswerDisqualified,
  isScreeningAnswerValid
} from './validation';

export default function ScreeningQuestions() {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const {
    screeningQuestions,
    updateScreeningQuestionAnswer,
    screeningQuestionsError
  } = useMultiplePricesStore();
  const orderId = detailData?.order?.id ?? '';

  const handleSingleChoiceChange = (
    index: number,
    question: ScreeningQuestion,
    answer: string
  ) => {
    const previousAnswer =
      useMultiplePricesStore.getState().screeningQuestions[index]?.answer;
    updateScreeningQuestionAnswer(index, answer);

    const campaignId = detailData?.order?.campaignId;
    if (
      previousAnswer === answer ||
      !orderId ||
      !campaignId ||
      !isScreeningAnswerDisqualified({ ...question, answer })
    ) {
      return;
    }

    GA.gtag('event', 'select_disqualifying_answer', {
      order_id: orderId,
      campaign_id: campaignId,
      order_source: getOrderSource(orderId),
      category: detailData?.order?.platform,
      isAhaPicksOffer: detailData?.order?.ahaPicksOffer ? 1 : 0
    });
  };

  if (!screeningQuestions?.length) return null;

  // 只有文本题的已有表单保留原布局；包含单选题时使用新版 Figma 布局。
  const hasSingleChoice = screeningQuestions.some(
    (question) => question.answerType === 'single_choice'
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      {!hasSingleChoice && (
        <div className="justify-start text-[#0D0D0D] text-base font-semibold font-['Inter'] leading-6">
          {t('screening_questions.title', {
            defaultValue: 'Screening questions'
          })}
        </div>
      )}
      <div
        className={`flex min-w-0 flex-col ${hasSingleChoice ? 'gap-6' : 'gap-3 pr-4'}`}
        id="take-order-screening-questions"
      >
        {screeningQuestions.map((question, index) => {
          const isSingleChoice = question.answerType === 'single_choice';
          const isDisqualified = isScreeningAnswerDisqualified(question);
          const showError =
            screeningQuestionsError && !isScreeningAnswerValid(question);

          return (
            <div
              id={`take-order-screening-question-${index}`}
              className={`flex min-w-0 flex-col ${hasSingleChoice ? 'gap-2' : 'gap-1.5'}`}
              key={`${question.question}-${index}`}
              data-screening-question-index={index}
              tabIndex={-1}
            >
              <div className="inline-flex min-w-0 items-start gap-0.5">
                <RichTextHtmlViewer
                  html={question.question}
                  className={`simple-rich-text-show-style min-w-0 font-['DM_Sans'] ${
                    hasSingleChoice
                      ? 'break-words text-[#292524] text-base font-semibold leading-6 tracking-[-0.16px]'
                      : 'flex-1 text-[#44403C] text-sm font-medium leading-5'
                  }`}
                />
                <span
                  className={`shrink-0 text-[#EC470A] text-sm font-medium font-['DM_Sans'] ${hasSingleChoice ? 'leading-6' : 'leading-5'}`}
                >
                  *
                </span>
              </div>
              {isSingleChoice ? (
                <Select
                  error={showError || isDisqualified}
                  value={question.answer ?? ''}
                  options={(question.options ?? []).map((option) => ({
                    label: option,
                    value: option
                  }))}
                  onChange={(value) =>
                    handleSingleChoiceChange(index, question, value as string)
                  }
                  placeholder={t('screening_questions.select_placeholder', {
                    defaultValue: 'Please select'
                  })}
                  size="medium"
                  className={`!min-w-0 ${showError || isDisqualified ? '!border-red-500' : '!border-[#D4D4D4]'} !pl-[14px] !font-['Inter'] [&>div>span]:!font-medium ${question.answer ? '[&>div>span]:!text-[#171717]' : ''} [&>div>svg]:!h-5 [&>div>svg]:!w-5`}
                  dropdownStyle={{ zIndex: 10000 }}
                />
              ) : (
                <SimpleRichText
                  error={showError}
                  orderId={orderId}
                  value={question.answer ?? ''}
                  saveData={(value) =>
                    updateScreeningQuestionAnswer(index, value)
                  }
                  placeholder={t('screening_questions.input_placeholder', {
                    defaultValue: 'Input your answer'
                  })}
                />
              )}
              {isDisqualified ? (
                <DisqualifiedAnswerAlert />
              ) : showError ? (
                <div role="alert" className="text-red-500 text-sm">
                  {t('screening_questions.required_error', {
                    defaultValue: 'This is a required field.'
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
